import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Portal } from '@trinity/chat-components/components/Portal';
import {
  DropdownMenu,
  DropdownMenuAnchor,
  DropdownMenuItem,
} from '@trinity/chat-components/components/presentation/DropdownMenu';
import {
  useZIndexMiddleware,
  useSameWidthMiddleware,
} from '@trinity/chat-components/components/presentation/popper-modifiers';
import { Constants } from '@trinity/chat-components/components/Theme';
import { useChatAgentMenuItems } from './hooks/useChatAgentMenuItems';
import { useContextSelectoryDisplayValue } from './hooks/useContextSelectoryDisplayValue';
import { ContextSelectorStyles } from './styles';

// --- Presentation ---

type SimpleContextSelectorPresentationProps = {
  referenceRef: (node: HTMLElement | null) => void;
  floatingRef: (node: HTMLElement | null) => void;
  floatingStyles: CSSProperties;
  anchorOpen: boolean;
  onAnchorClick: (e: React.MouseEvent) => void;
  onClickAway: () => void;
  label?: ReactNode;
  menuItems?: ReactNode;
};

function SimpleContextSelectorPresentation({
  referenceRef,
  floatingRef,
  floatingStyles,
  anchorOpen,
  onAnchorClick,
  onClickAway,
  label,
  menuItems,
}: SimpleContextSelectorPresentationProps): ReactElement {
  return (
    <Root>
      <Wrap>
        <StyledDropdownMenuAnchor ref={referenceRef} onClick={onAnchorClick} open={anchorOpen}>
          {label}
        </StyledDropdownMenuAnchor>
      </Wrap>
      {menuItems && (
        <Portal>
          <ClickAwayListener onClickAway={onClickAway}>
            <StyledDropdownMenu ref={floatingRef} style={floatingStyles}>
              {menuItems}
            </StyledDropdownMenu>
          </ClickAwayListener>
        </Portal>
      )}
    </Root>
  );
}

// --- Controller ---

function SimpleContextSelectorController(): ReactElement {
  const [open, setOpen] = useState<boolean>(false);

  const zIndexMiddleware = useZIndexMiddleware();
  const sameWidthMiddleware = useSameWidthMiddleware();
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [zIndexMiddleware, sameWidthMiddleware],
  });

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleItemClick = useCallback(() => {
    handleBlur();
  }, [handleBlur]);

  const agentMenuItems = useChatAgentMenuItems({
    onClick: handleItemClick,
  });

  const handleClick = useCallback(
    (_: React.MouseEvent) => {
      if (!agentMenuItems.length) return;

      setOpen((prev) => !prev);
    },
    [agentMenuItems.length]
  );

  const label = useContextSelectoryDisplayValue();

  const menuItems = open
    ? agentMenuItems.map((item) => (
        <StyledDropdownMenuItem key={item.key} onClick={item.onClick}>
          {item.label}
        </StyledDropdownMenuItem>
      ))
    : undefined;

  return (
    <SimpleContextSelectorPresentation
      referenceRef={refs.setReference}
      floatingRef={refs.setFloating}
      floatingStyles={floatingStyles}
      anchorOpen={open}
      onAnchorClick={handleClick}
      onClickAway={handleBlur}
      label={label}
      menuItems={menuItems}
    />
  );
}

export { SimpleContextSelectorController as SimpleContextSelector };
export { SimpleContextSelectorPresentation };
export type { SimpleContextSelectorPresentationProps };

// --- Styles ---

const Root = styled.div({});

const Wrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

const StyledDropdownMenu = styled(DropdownMenu)({
  maxWidth: 400,
});

const StyledDropdownMenuAnchor = styled(DropdownMenuAnchor)(({ theme }) => ({
  height: Constants.Header.ItemHeight,
  color: theme.palette.black.main,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...ContextSelectorStyles,
}));

const StyledDropdownMenuItem = styled(DropdownMenuItem)({
  textAlign: 'left',
  wordWrap: 'break-word',
  wordBreak: 'break-all',
  ...ContextSelectorStyles,
});
